import React, { useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { Modal, Button, ActionBar } from '@/components/common';

interface SessionReplacedModalProps {
  isOpen: boolean;
  /** Take the session back on this device. Rejects when the island refuses the join. */
  onReclaim: () => Promise<void>;
  /** Give the session up and go back to the island list. */
  onLeave: () => void;
}

/** Nothing closes this modal but one of its two answers. */
const IGNORE_DISMISS = () => {};

/**
 * Shown on a device whose session was taken over by the same account on
 * another device or tab (#132).
 *
 * It blocks on purpose: the island behind it is inert, so dismissing it would
 * leave the user on a screen where nothing works. Taking the session back ends
 * it on the other device, which then shows this same modal.
 */
export const SessionReplacedModal: React.FC<SessionReplacedModalProps> = ({
  isOpen,
  onReclaim,
  onLeave
}) => {
  const [isReclaiming, setIsReclaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReclaim = async () => {
    setIsReclaiming(true);
    setError(null);
    try {
      await onReclaim();
    } catch (reclaimError) {
      setError(reclaimError instanceof Error ? reclaimError.message : 'Could not reconnect');
    } finally {
      setIsReclaiming(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={IGNORE_DISMISS}
      title="Minka is open elsewhere"
      showCloseButton={false}
      size="md"
    >
      <div className="py-5 space-y-3 text-gray-300">
        <p>You opened Minka on another device or tab, so this one was disconnected.</p>
        <p className="text-sm text-gray-400">
          Use Minka here to continue on this device from where you are on the other one.
          The other one will be disconnected.
        </p>
        {error && (
          <p role="alert" className="flex items-center gap-2 text-sm text-game-orange">
            <FaExclamationCircle className="flex-shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
      </div>

      <ActionBar align="right" className="pt-4 border-t border-game-medium/30">
        <Button variant="secondary" onClick={onLeave} disabled={isReclaiming} className="px-6">
          Back to islands
        </Button>
        <Button variant="primary" onClick={handleReclaim} isLoading={isReclaiming} className="px-6">
          Use Minka here
        </Button>
      </ActionBar>
    </Modal>
  );
};
